import React, { useEffect } from 'react';
import { Home, RotateCcw, Trophy, Star } from 'lucide-react';
import { playSound } from '../../utils/soundManager';
import { useLanguage } from '../../contexts/LanguageContext';

const QuizResults = ({ category, score, totalQuestions, answers, onRestart, onHome }) => {
  const { t } = useLanguage();
  const percentage = Math.round((score / totalQuestions) * 100);

  useEffect(() => {
    if (percentage === 100) {
      playSound('complete');
    } else if (percentage >= 70) {
      playSound('success');
    }
  }, [percentage]);

  const getPerformanceMessage = () => {
    if (percentage === 100) return { text: t('results.perfectScore'), emoji: '🏆', color: '#f39c12' };
    if (percentage >= 80) return { text: t('results.excellent'), emoji: '⭐', color: '#2ecc71' };
    if (percentage >= 60) return { text: t('results.goodJob'), emoji: '👍', color: '#3498db' };
    return { text: t('results.keepTrying'), emoji: '💪', color: '#e74c3c' };
  };

  const performance = getPerformanceMessage();

  return (
    <div className="results-container animate-fadeIn">
      <div className="results-card">
        <div className="results-header">
          <div className="performance-icon animate-bounce" style={{ fontSize: '4rem' }}>
            {performance.emoji}
          </div>
          <h2 className="animate-scaleIn" style={{ color: performance.color }}>
            {performance.text}
          </h2>
        </div>

        <div className="score-display animate-scaleIn">
          <div className="score-circle" style={{ borderColor: performance.color }}>
            <div className="score-number" style={{ color: performance.color }}>
              {percentage}%
            </div>
            <div className="score-label">{t('quiz.score')}</div>
          </div>
          <div className="score-details">
            <div className="score-stat">
              <Trophy size={24} style={{ color: '#f39c12' }} />
              <span>{score} / {totalQuestions} {t('quiz.correct')}</span>
            </div>
            <div className="score-stat">
              <Star size={24} style={{ color: '#3498db' }} />
              <span style={{ textTransform: 'capitalize' }}>{t(`categories.${category}`)} Quiz</span>
            </div>
          </div>
        </div>

        {/* Answer Review */}
        <div className="answer-review">
          <h3>{t('quiz.reviewAnswers')}</h3>
          <div className="answers-list">
            {answers.map((answer, index) => (
              <div 
                key={index} 
                className={`answer-item animate-slideInLeft ${
                  answer.isCorrect ? 'answer-correct' : 'answer-incorrect'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="answer-number">{index + 1}</div>
                <div className="answer-content">
                  <div className="answer-question">{answer.question}</div>
                  <div className="answer-details">
                    {answer.isCorrect ? (
                      <span className="correct-text">
                        {t('quiz.yourAnswer')} {answer.selected} ✅
                      </span>
                    ) : (
                      <>
                        <span className="incorrect-text">
                          {t('quiz.yourAnswer')} {answer.selected}
                        </span>
                        <br />
                        <span className="correct-answer">
                          {t('quiz.correctAnswer')} {answer.correct}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="answer-icon">
                  {answer.isCorrect ? '✅' : '❌'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="results-actions">
          <button 
            className="action-button secondary hover-lift btn-press"
            onClick={onHome}
          >
            <Home size={20} />
            {t('common.backToHome')}
          </button>
          <button 
            className="action-button primary hover-lift btn-press"
            onClick={onRestart}
            style={{ background: performance.color }}
          >
            <RotateCcw size={20} />
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;
